/*
 * The live-run surfaces: the phase spine, the live/paused indicator, and the
 * in-flight header panel.
 *
 * Focus rule, applied deliberately: nothing in this file (or the stream it
 * accompanies) ever calls scrollIntoView, focus() or scrollTo. New activity
 * appends chronologically at the bottom of the stream, so content above the
 * reader's viewport never changes height and the browser's scroll anchoring
 * keeps their position still. The reader opts into movement (Resume, or
 * scrolling themselves) — the page never moves for them.
 */
import type { WebTestRun } from '@blume/web-test-model'
import {
  Ban,
  Circle,
  CircleCheck,
  Eye,
  LoaderCircle,
  Pause,
  Play,
  TriangleAlert
} from 'lucide-react'

import { originLabel, targetLabel } from '@/design/format'
import { cn } from '@/lib/utils'
import type { PhaseSpine as PhaseSpineModel, SpinePhase } from '@/live/live-model'
import type { RunFeed } from '@/live/use-run-feed'

/* ------------------------------------------------------------------ */
/* Phase spine                                                         */
/* ------------------------------------------------------------------ */

function phaseIcon(phase: SpinePhase) {
  if (phase.status === 'done') {
    return <CircleCheck aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
  }
  if (phase.status === 'current') {
    return (
      <LoaderCircle
        aria-hidden
        className="size-3.5 shrink-0 text-status-info motion-safe:animate-spin"
      />
    )
  }
  return <Circle aria-hidden className="size-3.5 shrink-0 text-muted-foreground/50" />
}

const phaseStatusWord: Record<SpinePhase['status'], string> = {
  done: 'done',
  current: 'now',
  upcoming: 'next',
  'not-reached': 'not reached'
}

/**
 * Where the run is in its lifecycle. The one distinction that matters most —
 * is the agent still working the test out, or already replaying a trusted
 * plan? — is answered in the sentence above the spine, and the authoring
 * phases carry a visible bracket label.
 */
export function PhaseSpine({
  spine,
  showSentence = true
}: {
  spine: PhaseSpineModel
  showSentence?: boolean
}) {
  const authoringIds = new Set(['exploring', 'compiling', 'stabilizing'])
  const currentPhase = spine.phases.find((phase) => phase.status === 'current')
  return (
    <div>
      {showSentence ? <p className="text-[15px] leading-7">{spine.sentence}</p> : null}
      <ol aria-label="Run phases" className="mt-3 flex flex-wrap items-center gap-y-2">
        {spine.phases.map((phase, index) => {
          const authoring = spine.kind === 'authoring' && authoringIds.has(phase.id)
          const firstAuthoring =
            authoring && spine.phases.findIndex((p) => authoringIds.has(p.id)) === index
          return (
            <li key={phase.id} className="flex items-center">
              {index > 0 ? (
                <span aria-hidden className="mx-2 h-px w-3 shrink-0 bg-border sm:w-5" />
              ) : null}
              <span
                aria-current={phase.status === 'current' ? 'step' : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 text-[13px] font-medium',
                  phase.status === 'current'
                    ? 'text-status-info'
                    : phase.status === 'done'
                      ? 'text-foreground'
                      : 'text-muted-foreground/70',
                  authoring && 'rounded-sm bg-status-info-surface/40 px-1.5 py-0.5',
                  firstAuthoring && 'relative'
                )}
              >
                {phaseIcon(phase)}
                {phase.label}
                <span className="sr-only"> — {phaseStatusWord[phase.status]}</span>
              </span>
            </li>
          )
        })}
      </ol>
      {spine.kind === 'authoring' ? (
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          Tinted phases are the agent authoring the test; the rest is platform lifecycle.
        </p>
      ) : null}
      {currentPhase ? (
        <p className="mt-1 text-[13px] text-muted-foreground">
          {currentPhase.label} — {currentPhase.hint}.
        </p>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Live / paused indicator                                             */
/* ------------------------------------------------------------------ */

function agoLabel(timestamp: number | undefined): string | undefined {
  if (timestamp === undefined) return undefined
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1_000))
  if (seconds < 10) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  return `${Math.round(seconds / 60)}m ago`
}

/**
 * The honest liveness chip: a real feed says Live/Paused/Interrupted; the
 * fixture simulation is always labelled as demo data and never claims to be
 * live. Includes the pause/resume control — pausing freezes the page and
 * buffers activity; nothing updates until the reader resumes.
 */
export function LiveStatusControls({ feed }: { feed: RunFeed }) {
  const finished = feed.liveness === 'finished'
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <LivenessChip feed={feed} />
      {!finished && feed.liveness !== 'loading' ? (
        <button
          type="button"
          onClick={feed.paused ? feed.resume : feed.pause}
          className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
        >
          {feed.paused ? (
            <>
              <Play aria-hidden className="size-3.5" />
              Resume updates
              {feed.pendingCount > 0 ? (
                <span className="text-muted-foreground">
                  · {feed.pendingCount} waiting
                </span>
              ) : null}
            </>
          ) : (
            <>
              <Pause aria-hidden className="size-3.5" />
              Pause updates
            </>
          )}
        </button>
      ) : null}
    </div>
  )
}

function LivenessChip({ feed }: { feed: RunFeed }) {
  if (feed.simulated) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
        <Eye aria-hidden className="size-3.5" />
        Demo data — progression is simulated, not a live feed
      </span>
    )
  }
  if (feed.liveness === 'paused') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
        <Pause aria-hidden className="size-3.5" />
        Paused
        {feed.pendingCount > 0 ? ` — ${feed.pendingCount} new updates waiting` : ''}
      </span>
    )
  }
  if (feed.liveness === 'degraded') {
    const ago = agoLabel(feed.lastUpdatedAt)
    return (
      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-status-warn">
        <TriangleAlert aria-hidden className="size-3.5" />
        Live updates interrupted — retrying{ago ? ` · last update ${ago}` : ''}
      </span>
    )
  }
  if (feed.liveness === 'finished') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
        <CircleCheck aria-hidden className="size-3.5" />
        This run finished while you were watching — the verdict above is final
      </span>
    )
  }
  if (feed.liveness === 'loading') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
        <LoaderCircle aria-hidden className="size-3.5 motion-safe:animate-spin" />
        Connecting to the verifier…
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-status-info">
      <span
        aria-hidden
        className="size-2 rounded-full bg-status-info motion-safe:animate-pulse"
      />
      Live — updates append below as they arrive
    </span>
  )
}

/* ------------------------------------------------------------------ */
/* In-flight header panel                                              */
/* ------------------------------------------------------------------ */

/** "Started by claude-code over MCP · targeting Local Web Lab". */
export function runOriginSentence(run: WebTestRun): string {
  const origin = originLabel[run.origin] ?? run.origin
  const target = targetLabel[run.target] ?? run.target
  return `Started by ${run.initiatedBy} over ${origin} · targeting ${target}`
}

export function CancellingNotice() {
  return (
    <p className="flex items-start gap-2 rounded-md bg-muted px-3 py-2.5 text-sm leading-6">
      <Ban aria-hidden className="mt-1 size-3.5 shrink-0 text-muted-foreground" />
      <span>
        <span className="font-medium">Cancel requested.</span>{' '}
        <span className="text-muted-foreground">
          The run is stopping safely — the browser is being torn down and evidence captured so far
          will be kept.
        </span>
      </span>
    </p>
  )
}

/**
 * The screen-reader narration for a live run. Announces phase changes only —
 * one polite sentence per lifecycle transition, never one per event.
 */
export function LiveAnnouncement({ sentence }: { sentence: string }) {
  return (
    <p role="status" aria-live="polite" className="sr-only">
      {sentence}
    </p>
  )
}
